import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ProgressZone } from 'src/app/_shared/components/utils/circular-progress/circular-progress.component';
import { VehicleGeneralFigureService } from 'src/app/_services/vehicle-general-figure.service';
import { NavigationService } from 'src/app/_services/navigation.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { imageUrls } from 'src/app/_shared/utils/images';

@Component({
  selector: 'app-ev-atomic-aging',
  templateUrl: './ev-atomic-aging.component.html',
  styleUrls: ['./ev-atomic-aging.component.scss']
})
export class EvAtomicAgingComponent implements OnChanges, OnDestroy {
  @Input() batteryId?: string;
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;

  backgroundZones: ProgressZone[] = [
    { from: 0, to: 60, color: 'var(--bosch-gray-80)' },
    { from: 60, to: 80, color: 'var(--bosch-blue-80)' },
    { from: 80, to: 100, color: 'var(--bosch-green-80)' }
  ];

  stressFactors: any[] = [];
  detailTitle: string | null = null;

  imageUrls = imageUrls;
  private readonly unsubscribe$ = new Subject<void>();
  private readonly kmToMilesPipe = new KmToMilesPipe();
  private readonly MAIN_FACTORS = [
    'Share of Fast Charging Sessions',
    'Charging at Low Temperatures',
    'State of Charge (SoC) > 80%',
    'High Battery Temperature'
  ];
  private readonly FACTOR_NAME_ALIASES: Record<string, string[]> = {
    'Share of Fast Charging Sessions': ['Share of Fast Charging Sessions', 'High Power Charging'],
    'Charging at Low Temperatures': ['Charging at Low Temperatures'],
    'State of Charge (SoC) > 80%': ['State of Charge (SoC) > 80%', 'highSoc'],
    'High Battery Temperature': ['High Battery Temperature', 'Temperature > 30\u00b0C']
  };

  constructor(
    private readonly vehicleGeneralFigureService: VehicleGeneralFigureService,
    private readonly navigationService: NavigationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['batteryId']) {
      this.loadForBatteryId(this.batteryId);
    }
  }

  loadForBatteryId(id?: string): void {
    if (!id) {
      this.stressFactors = [];
      return;
    }
    // call API for single battery id
    this.vehicleGeneralFigureService.getAtomicAging([id]).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (resp: any) => {
        // API returns map keyed by id (or variations). Try to find matching key
        let data = resp?.[id];
        if (!data) {
          const keys = Object.keys(resp || {});
          const trimmed = (id || '').trim();
          const foundKey = keys.find(k => k === trimmed || k === `${trimmed}_dev` || k.includes(trimmed) || trimmed.includes(k));
          if (foundKey) data = resp[foundKey];
        }
        const vehicleFactors: any[] = data?.stressFactors ?? [];
        this.stressFactors = this.MAIN_FACTORS
          .map(name => {
            const found = (vehicleFactors || []).find(f => this.factorNamesMatch(f.stressFactorName, name));
            if (!found || found.score == null) {
              return null;
            }

            return {
              stressFactorName: name,
              score: found.score ?? null,
              deltaScore: found.deltaScore ?? null,
              loss: found.loss ?? null,
              rating: found.rating ?? null,
              ratingIntervals: found.ratingIntervals ?? [0, 60, 80, 100],
              warningIcon: found.warningIcon ?? false
            };
          })
          .filter((factor): factor is any => factor !== null);
      },
      error: err => {
        console.error('Error fetching EV atomic aging:', err);
        this.stressFactors = [];
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  nameMatches(a: string | undefined, b: string | undefined): boolean {
    if (!a || !b) return false;
    const normalize = (s: string) => s.replace(/[^a-z0-9]/gi, '').toLowerCase();
    return normalize(a).includes(normalize(b)) || normalize(b).includes(normalize(a));
  }

  private factorNamesMatch(apiName: string | undefined, uiName: string | undefined): boolean {
    if (!apiName || !uiName) return false;
    const aliases = this.FACTOR_NAME_ALIASES[uiName] ?? [uiName];
    return aliases.some(alias => this.nameMatches(apiName, alias));
  }

  getStressFactorsRowClass(): string {
    const count = this.stressFactors?.length ?? 0;
    const boundedCount = Math.min(Math.max(count, 1), 4);
    return `row-cols-${boundedCount}`;
  }

  // no-op to match template button (keeps parity with fleet component)
  toggleDetails(_sf: any): void {
    this.detailTitle = this.detailTitle ? null : _sf?.stressFactorName ?? null;
  }

  getColor(rating: string): string {
    if (!rating) return 'var(--bosch-blue-50)';
    switch (rating.toLowerCase()) {
      case 'optimal': return 'var(--bosch-green-50)';
      case 'high': return 'var(--bosch-green-50)';
      case 'medium': return 'var(--bosch-blue-50)';
      case 'mid': return 'var(--bosch-blue-50)';
      case 'low': return 'var(--bosch-purple-50)';
      default: return 'var(--bosch-blue-50)';
    }
  }

  getZones(ratingIntervals: number[] | undefined): ProgressZone[] {
    if (!Array.isArray(ratingIntervals) || ratingIntervals.length < 2) {
      return this.backgroundZones;
    }
    const ranges: ProgressZone[] = [];
    const colors = ['var(--bosch-gray-80)', 'var(--bosch-blue-80)', 'var(--bosch-green-80)'];
    for (let i = 0; i < ratingIntervals.length - 1; i++) {
      ranges.push({
        from: ratingIntervals[i],
        to: ratingIntervals[i + 1],
        color: colors[Math.min(i, colors.length - 1)]
      });
    }
    return ranges;
  }

  getBgColor(name: string | null | undefined): string {
    if (!name) return 'var(--bosch-yellow-90)';
    switch (name) {
      case 'Share of Fast Charging Sessions':
        return 'var(--bosch-red-90)';
      case 'Charging at Low Temperatures':
        return 'var(--bosch-blue-90)';
      case 'State of Charge (SoC) > 80%':
        return 'var(--bosch-purple-90)';
      case 'High Battery Temperature':
        return 'var(--bosch-yellow-90)';
      default:
        return 'var(--bosch-yellow-90)';
    }
  }

  getConvertedValuesByUnit(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kmToMilesPipe.transform(val))
        : arr;
    }
  }

  getMileageUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'Mileage [km]' : 'Mileage [mi]';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'Mileage [mi]'
        : 'Mileage [km]';
    }
  }

  formatLoss(loss: number | null | undefined): string {
    if (loss === null || loss === undefined) return 'N/A';
    const converted = this.getConvertedValuesByUnit([loss])[0];
    const unit = this.getMileageUnit().includes('mi') ? 'mi' : 'km';
    return converted != null ? (converted.toFixed(2) + unit + ' (In 1 Year)') : 'N/A';
  }

  goToInsights(vehicleId: string) {
    this.navigationService.navigateTo('insights', vehicleId);
  }

}
